import { useState, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Users, FileText, CreditCard, BarChart3, ShieldCheck, ShieldX, ArrowRight, Pencil, Save, Globe, TrendingUp } from "lucide-react";
import { toast } from "sonner";

const STAGE_LABELS = {
  nl: { lead: "Lead", quick_scan: "Quick Scan", pilot: "Pilot", workflow: "Workflow", training: "Training", governance: "Governance" },
  en: { lead: "Lead", quick_scan: "Quick Scan", pilot: "Pilot", workflow: "Workflow", training: "Training", governance: "Governance" },
};

const STAGE_ORDER = ["lead", "quick_scan", "pilot", "workflow", "training", "governance"];

const SENDGRID_STATUS_MSG = {
  nl: "SendGrid is geconfigureerd. Als er geen API-sleutel is ingesteld, worden e-mails opgeslagen maar niet verzonden.",
  en: "SendGrid is configured. If no API key is set, emails are stored but not sent.",
  de: "SendGrid ist konfiguriert. Ohne API-Key werden E-Mails gespeichert aber nicht gesendet.",
  fr: "SendGrid est configur\u00e9. Sans cl\u00e9 API, les e-mails sont stock\u00e9s mais non envoy\u00e9s.",
  es: "SendGrid est\u00e1 configurado. Sin clave API, los emails se almacenan pero no se env\u00edan.",
  ar: "\u062a\u0645 \u062a\u0643\u0648\u064a\u0646 SendGrid. \u0628\u062f\u0648\u0646 \u0645\u0641\u062a\u0627\u062d API\u060c \u064a\u062a\u0645 \u062a\u062e\u0632\u064a\u0646 \u0627\u0644\u0628\u0631\u064a\u062f \u0648\u0644\u0643\u0646 \u0644\u0627 \u064a\u062a\u0645 \u0625\u0631\u0633\u0627\u0644\u0647.",
};
const STAGE_COLORS = {
  lead: "bg-gray-100 text-gray-700",
  quick_scan: "bg-blue-50 text-blue-700",
  pilot: "bg-indigo-50 text-indigo-700",
  workflow: "bg-purple-50 text-purple-700",
  training: "bg-green-50 text-green-700",
  governance: "bg-red-50 text-red-700",
};

const SERVICE_NAMES = {
  "ai-quick-scan": "AI Quick Scan",
  "ai-pilot": "AI Pilot",
  "ai-workflow": "AI Workflow",
  "ai-training": "AI Training",
  "ai-governance": "AI Governance",
};

export default function AdminDashboard() {
  const { t, lang } = useLanguage();
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [pipeline, setPipeline] = useState({});
  const [intakes, setIntakes] = useState([]);
  const [payments, setPayments] = useState([]);
  const [reports, setReports] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingReport, setEditingReport] = useState(null);
  const [editContent, setEditContent] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      const [statsRes, clientsRes, pipelineRes, intakesRes, paymentsRes, reportsRes, bookingsRes, notifsRes, analyticsRes] = await Promise.all([
        API.get("/admin/stats"),
        API.get("/admin/clients"),
        API.get("/admin/pipeline"),
        API.get("/admin/intakes"),
        API.get("/admin/payments"),
        API.get("/admin/reports"),
        API.get("/admin/bookings"),
        API.get("/admin/notifications"),
        API.get("/admin/analytics").catch(() => ({ data: null })),
      ]);
      setStats(statsRes.data);
      setClients(clientsRes.data);
      setPipeline(pipelineRes.data);
      setIntakes(intakesRes.data);
      setPayments(paymentsRes.data);
      setReports(reportsRes.data);
      setBookings(bookingsRes.data);
      setNotifications(notifsRes.data);
      setAnalytics(analyticsRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleBlock = async (userId) => {
    try {
      await API.put(`/admin/clients/${userId}/block`);
      toast.success("Client blocked");
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const handleAccept = async (userId) => {
    try {
      await API.put(`/admin/clients/${userId}/accept`);
      toast.success("Client accepted");
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const handleStageChange = async (userId, stage) => {
    try {
      await API.put(`/admin/pipeline/${userId}/stage`, { stage });
      toast.success(`Moved to ${stage}`);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const handleBookingStatus = async (bookingId, status) => {
    try {
      await API.put(`/admin/bookings/${bookingId}/status`, { status });
      toast.success(`Booking ${status}`);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const handleEditReport = (report) => {
    setEditingReport(report);
    setEditContent(report.content || "");
  };

  const handleSaveReport = async () => {
    if (!editingReport) return;
    setSaving(true);
    try {
      await API.put(`/admin/reports/${editingReport.report_id}`, { content: editContent });
      toast.success(lang === "nl" ? "Rapport bijgewerkt" : "Report updated");
      setEditingReport(null);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error saving report");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <div className="w-6 h-6 border-2 border-[#0A3FA3] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-12 bg-[#F9FAFB]" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-8" data-testid="admin-title">{t("admin.title")}</h1>

        <Tabs defaultValue="stats" className="space-y-6">
          <TabsList className="bg-white border border-border rounded-sm flex-wrap h-auto gap-0">
            <TabsTrigger value="stats" className="rounded-sm text-xs" data-testid="admin-tab-stats">{t("admin.tab_stats")}</TabsTrigger>
            <TabsTrigger value="clients" className="rounded-sm text-xs" data-testid="admin-tab-clients">{t("admin.tab_clients")}</TabsTrigger>
            <TabsTrigger value="pipeline" className="rounded-sm text-xs" data-testid="admin-tab-pipeline">{t("admin.tab_pipeline")}</TabsTrigger>
            <TabsTrigger value="intakes" className="rounded-sm text-xs" data-testid="admin-tab-intakes">{t("admin.tab_intakes")}</TabsTrigger>
            <TabsTrigger value="reports" className="rounded-sm text-xs" data-testid="admin-tab-reports">{t("admin.tab_reports")}</TabsTrigger>
            <TabsTrigger value="payments" className="rounded-sm text-xs" data-testid="admin-tab-payments">{t("admin.tab_payments")}</TabsTrigger>
            <TabsTrigger value="bookings" className="rounded-sm text-xs" data-testid="admin-tab-bookings">{lang === "nl" ? "Afspraken" : "Bookings"}</TabsTrigger>
            <TabsTrigger value="notifications" className="rounded-sm text-xs" data-testid="admin-tab-notifications">{lang === "nl" ? "Notificaties" : "Notifications"}</TabsTrigger>
            <TabsTrigger value="analytics" className="rounded-sm text-xs" data-testid="admin-tab-analytics">
              <Globe size={12} className="mr-1" />{lang === "nl" ? "A/B Taal" : lang === "de" ? "A/B Sprache" : lang === "fr" ? "A/B Langue" : "A/B Language"}
            </TabsTrigger>
          </TabsList>

          {/* Stats */}
          <TabsContent value="stats">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="admin-stats-grid">
              {[
                { label: t("admin.total_clients"), value: stats?.total_clients, icon: Users },
                { label: t("admin.active_clients"), value: stats?.active_clients, icon: ShieldCheck },
                { label: t("admin.total_intakes"), value: stats?.total_intakes, icon: FileText },
                { label: t("admin.total_revenue"), value: `\u20ac${(stats?.total_revenue || 0).toLocaleString()}`, icon: CreditCard },
                { label: t("admin.paid_intakes"), value: stats?.paid_intakes, icon: BarChart3 },
                { label: t("admin.total_reports"), value: stats?.total_reports, icon: FileText },
                { label: t("admin.blocked_clients"), value: stats?.blocked_clients, icon: ShieldX },
              ].map((s, i) => (
                <div key={i} className="bg-white border border-border p-5" data-testid={`stat-card-${i}`}>
                  <div className="flex items-center justify-between mb-3">
                    <s.icon size={18} className="text-[#0A3FA3]" />
                  </div>
                  <div className="text-2xl font-bold">{s.value}</div>
                  <div className="text-xs text-muted-foreground mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Clients */}
          <TabsContent value="clients">
            <div className="bg-white border border-border">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="clients-table">
                  <thead>
                    <tr className="border-b border-border bg-[#F9FAFB]">
                      <th className="text-left p-3 text-xs font-bold uppercase tracking-wider">Name</th>
                      <th className="text-left p-3 text-xs font-bold uppercase tracking-wider">Email</th>
                      <th className="text-left p-3 text-xs font-bold uppercase tracking-wider">Stage</th>
                      <th className="text-left p-3 text-xs font-bold uppercase tracking-wider">{t("dashboard.status")}</th>
                      <th className="text-left p-3 text-xs font-bold uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clients.map((c) => (
                      <tr key={c.user_id} className="border-b border-border last:border-0" data-testid={`client-row-${c.user_id}`}>
                        <td className="p-3 font-medium">{c.name || "-"}</td>
                        <td className="p-3 text-muted-foreground">{c.email}</td>
                        <td className="p-3">
                          <span className={`inline-block px-2 py-0.5 text-xs font-bold rounded-sm ${STAGE_COLORS[c.pipeline_stage] || "bg-gray-100"}`}>
                            {STAGE_LABELS[lang]?.[c.pipeline_stage] || c.pipeline_stage}
                          </span>
                        </td>
                        <td className="p-3">
                          <Badge variant={c.status === "active" ? "default" : "destructive"} className="text-[10px] rounded-sm" data-testid={`client-status-${c.user_id}`}>
                            {c.status === "active" ? t("admin.active") : t("admin.blocked")}
                          </Badge>
                        </td>
                        <td className="p-3">
                          {c.status === "active" ? (
                            <Button size="sm" variant="outline" className="text-xs rounded-sm text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleBlock(c.user_id)} data-testid={`block-btn-${c.user_id}`}>
                              {t("admin.block")}
                            </Button>
                          ) : (
                            <Button size="sm" variant="outline" className="text-xs rounded-sm text-green-600 border-green-200 hover:bg-green-50" onClick={() => handleAccept(c.user_id)} data-testid={`accept-btn-${c.user_id}`}>
                              {t("admin.accept")}
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                    {clients.length === 0 && (
                      <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">No clients yet</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* CRM Pipeline */}
          <TabsContent value="pipeline">
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="pipeline-grid">
              {STAGE_ORDER.map((stage) => (
                <div key={stage} className="bg-white border border-border" data-testid={`pipeline-col-${stage}`}>
                  <div className={`p-3 border-b border-border ${STAGE_COLORS[stage]}`}>
                    <div className="text-xs font-bold uppercase tracking-wider">{STAGE_LABELS[lang]?.[stage] || stage}</div>
                    <div className="text-lg font-bold">{(pipeline[stage] || []).length}</div>
                  </div>
                  <div className="p-2 space-y-2 max-h-[400px] overflow-y-auto">
                    {(pipeline[stage] || []).map((c) => (
                      <div key={c.user_id} className={`p-3 border border-border pipeline-${stage}`} data-testid={`pipeline-card-${c.user_id}`}>
                        <div className="text-sm font-medium truncate">{c.name || c.email}</div>
                        <div className="text-xs text-muted-foreground truncate">{c.email}</div>
                        <div className="mt-2">
                          <Select onValueChange={(val) => handleStageChange(c.user_id, val)}>
                            <SelectTrigger className="h-7 text-xs rounded-sm" data-testid={`stage-select-${c.user_id}`}>
                              <SelectValue placeholder={t("admin.move_to")} />
                            </SelectTrigger>
                            <SelectContent>
                              {STAGE_ORDER.filter((s) => s !== stage).map((s) => (
                                <SelectItem key={s} value={s} className="text-xs">{STAGE_LABELS[lang]?.[s] || s}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    ))}
                    {(pipeline[stage] || []).length === 0 && (
                      <div className="p-3 text-xs text-muted-foreground text-center">-</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Intakes */}
          <TabsContent value="intakes">
            <div className="bg-white border border-border">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="intakes-table">
                  <thead>
                    <tr className="border-b border-border bg-[#F9FAFB]">
                      <th className="text-left p-3 text-xs font-bold uppercase tracking-wider">Client</th>
                      <th className="text-left p-3 text-xs font-bold uppercase tracking-wider">Service</th>
                      <th className="text-left p-3 text-xs font-bold uppercase tracking-wider">{t("dashboard.payment")}</th>
                      <th className="text-left p-3 text-xs font-bold uppercase tracking-wider">{t("dashboard.report")}</th>
                      <th className="text-left p-3 text-xs font-bold uppercase tracking-wider">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {intakes.map((i) => (
                      <tr key={i.intake_id} className="border-b border-border last:border-0" data-testid={`admin-intake-${i.intake_id}`}>
                        <td className="p-3">
                          <div className="font-medium">{i.user_name || "-"}</div>
                          <div className="text-xs text-muted-foreground">{i.user_email}</div>
                        </td>
                        <td className="p-3">{SERVICE_NAMES[i.service_id] || i.service_id}</td>
                        <td className="p-3">
                          <Badge variant={i.payment_status === "paid" ? "default" : "secondary"} className="text-[10px] rounded-sm">
                            {i.payment_status}
                          </Badge>
                        </td>
                        <td className="p-3">
                          <Badge variant={i.report_status === "completed" ? "default" : "secondary"} className="text-[10px] rounded-sm">
                            {i.report_status}
                          </Badge>
                        </td>
                        <td className="p-3 text-muted-foreground">{new Date(i.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                    {intakes.length === 0 && (
                      <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">No submissions yet</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Reports */}
          <TabsContent value="reports">
            <div className="bg-white border border-border">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="admin-reports-table">
                  <thead>
                    <tr className="border-b border-border bg-[#F9FAFB]">
                      <th className="text-left p-3 text-xs font-bold uppercase tracking-wider">Report ID</th>
                      <th className="text-left p-3 text-xs font-bold uppercase tracking-wider">Service</th>
                      <th className="text-left p-3 text-xs font-bold uppercase tracking-wider">Lang</th>
                      <th className="text-left p-3 text-xs font-bold uppercase tracking-wider">Date</th>
                      <th className="text-left p-3 text-xs font-bold uppercase tracking-wider">Edited</th>
                      <th className="text-left p-3 text-xs font-bold uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reports.map((r) => (
                      <tr key={r.report_id} className="border-b border-border last:border-0">
                        <td className="p-3 font-mono text-xs">{r.report_id}</td>
                        <td className="p-3">{SERVICE_NAMES[r.service_id] || r.service_id}</td>
                        <td className="p-3 uppercase text-xs">{r.language}</td>
                        <td className="p-3 text-muted-foreground">{new Date(r.created_at).toLocaleDateString()}</td>
                        <td className="p-3">{r.edited_by_admin ? <Badge className="text-[10px] rounded-sm bg-green-50 text-green-700">Edited</Badge> : <span className="text-xs text-muted-foreground">-</span>}</td>
                        <td className="p-3">
                          <Button size="sm" variant="outline" className="text-xs rounded-sm h-7" onClick={() => handleEditReport(r)} data-testid={`edit-report-${r.report_id}`}>
                            <Pencil size={12} className="mr-1" /> {lang === "nl" ? "Bewerken" : "Edit"}
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {reports.length === 0 && (
                      <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">No reports yet</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Payments */}
          <TabsContent value="payments">
            <div className="bg-white border border-border">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="admin-payments-table">
                  <thead>
                    <tr className="border-b border-border bg-[#F9FAFB]">
                      <th className="text-left p-3 text-xs font-bold uppercase tracking-wider">Email</th>
                      <th className="text-left p-3 text-xs font-bold uppercase tracking-wider">Service</th>
                      <th className="text-left p-3 text-xs font-bold uppercase tracking-wider">Amount</th>
                      <th className="text-left p-3 text-xs font-bold uppercase tracking-wider">{t("dashboard.status")}</th>
                      <th className="text-left p-3 text-xs font-bold uppercase tracking-wider">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((p) => (
                      <tr key={p.transaction_id} className="border-b border-border last:border-0" data-testid={`admin-payment-${p.transaction_id}`}>
                        <td className="p-3">{p.email}</td>
                        <td className="p-3">{SERVICE_NAMES[p.service_id] || p.service_id}</td>
                        <td className="p-3 font-medium">&euro;{p.amount?.toLocaleString()}</td>
                        <td className="p-3">
                          <Badge variant={p.payment_status === "paid" ? "default" : "secondary"} className="text-[10px] rounded-sm">
                            {p.payment_status}
                          </Badge>
                        </td>
                        <td className="p-3 text-muted-foreground">{new Date(p.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                    {payments.length === 0 && (
                      <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">No payments yet</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Bookings */}
          <TabsContent value="bookings">
            <div className="bg-white border border-border">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="admin-bookings-table">
                  <thead>
                    <tr className="border-b border-border bg-[#F9FAFB]">
                      <th className="text-left p-3 text-xs font-bold uppercase tracking-wider">Client</th>
                      <th className="text-left p-3 text-xs font-bold uppercase tracking-wider">Service</th>
                      <th className="text-left p-3 text-xs font-bold uppercase tracking-wider">Date/Time</th>
                      <th className="text-left p-3 text-xs font-bold uppercase tracking-wider">Type</th>
                      <th className="text-left p-3 text-xs font-bold uppercase tracking-wider">Status</th>
                      <th className="text-left p-3 text-xs font-bold uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookings.map((b) => (
                      <tr key={b.booking_id} className="border-b border-border last:border-0" data-testid={`admin-booking-${b.booking_id}`}>
                        <td className="p-3"><div className="font-medium">{b.user_name || "-"}</div><div className="text-xs text-muted-foreground">{b.user_email}</div></td>
                        <td className="p-3">{SERVICE_NAMES[b.service_id] || b.service_id}</td>
                        <td className="p-3">{b.preferred_date} {b.preferred_time}</td>
                        <td className="p-3 capitalize text-xs">{b.booking_type}</td>
                        <td className="p-3"><Badge variant={b.status === "confirmed" ? "default" : "secondary"} className="text-[10px] rounded-sm">{b.status}</Badge></td>
                        <td className="p-3">
                          <div className="flex gap-1">
                            {b.status === "requested" && (
                              <>
                                <Button size="sm" variant="outline" className="text-xs rounded-sm text-green-600 border-green-200 h-7" onClick={() => handleBookingStatus(b.booking_id, "confirmed")} data-testid={`confirm-booking-${b.booking_id}`}>Confirm</Button>
                                <Button size="sm" variant="outline" className="text-xs rounded-sm text-red-600 border-red-200 h-7" onClick={() => handleBookingStatus(b.booking_id, "cancelled")} data-testid={`cancel-booking-${b.booking_id}`}>Cancel</Button>
                              </>
                            )}
                            {b.status === "confirmed" && (
                              <Button size="sm" variant="outline" className="text-xs rounded-sm h-7" onClick={() => handleBookingStatus(b.booking_id, "completed")}>Complete</Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {bookings.length === 0 && (
                      <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">No bookings yet</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Notifications */}
          <TabsContent value="notifications">
            <div className="bg-white border border-border">
              <div className="p-4 border-b border-border bg-[#F9FAFB]">
                <p className="text-xs text-muted-foreground">
                  {SENDGRID_STATUS_MSG[lang] || SENDGRID_STATUS_MSG.en}
                </p>
              </div>
              <div className="divide-y divide-border max-h-[500px] overflow-y-auto">
                {notifications.map((n, i) => (
                  <div key={n.notification_id || i} className="p-4 flex items-start gap-3" data-testid={`notification-${i}`}>
                    <div className="w-2 h-2 rounded-full bg-[#0A3FA3] mt-2 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-bold uppercase tracking-wider text-[#0A3FA3]">{n.type?.replace(/_/g, " ")}</span>
                        <span className="text-[10px] text-muted-foreground">{new Date(n.created_at).toLocaleString()}</span>
                      </div>
                      <div className="text-sm text-muted-foreground">{n.recipient_email}</div>
                      {n.data && <div className="text-xs text-muted-foreground/60 mt-1 truncate">{JSON.stringify(n.data)}</div>}
                    </div>
                    <Badge variant="secondary" className="text-[10px] rounded-sm flex-shrink-0">{n.status}</Badge>
                  </div>
                ))}
                {notifications.length === 0 && (
                  <div className="p-8 text-center text-muted-foreground">No notifications yet</div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* A/B Language Analytics */}
          <TabsContent value="analytics">
            <AnalyticsPanel analytics={analytics} lang={lang} />
          </TabsContent>
        </Tabs>

        {/* Report Edit Dialog */}
        <Dialog open={!!editingReport} onOpenChange={(open) => { if (!open) setEditingReport(null); }}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Pencil size={16} />
                {lang === "nl" ? "Rapport bewerken" : lang === "de" ? "Bericht bearbeiten" : lang === "fr" ? "Modifier le rapport" : lang === "es" ? "Editar informe" : lang === "ar" ? "\u062a\u062d\u0631\u064a\u0631 \u0627\u0644\u062a\u0642\u0631\u064a\u0631" : "Edit Report"} - {editingReport?.report_id}
              </DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto min-h-0">
              <p className="text-xs text-muted-foreground mb-3">
                {lang === "nl" ? "Bewerk de markdown inhoud van het rapport. De klant ziet de bijgewerkte versie." : "Edit the markdown content of the report. The client will see the updated version."}
              </p>
              <Textarea
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                rows={20}
                className="rounded-sm font-mono text-sm resize-none"
                data-testid="report-edit-textarea"
              />
            </div>
            <div className="flex justify-end gap-2 pt-4 border-t border-border">
              <Button variant="outline" className="rounded-sm text-xs" onClick={() => setEditingReport(null)} data-testid="report-edit-cancel">
                {lang === "nl" ? "Annuleren" : "Cancel"}
              </Button>
              <Button className="bg-[#0A3FA3] hover:bg-[#082F7A] rounded-sm text-xs" onClick={handleSaveReport} disabled={saving} data-testid="report-edit-save">
                <Save size={14} className="mr-1" /> {saving ? "..." : lang === "nl" ? "Opslaan" : "Save"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}


const LANG_FLAGS = { nl: "NL", en: "EN", de: "DE", fr: "FR", es: "ES", ar: "AR" };
const LANG_FULL = { nl: "Nederlands", en: "English", de: "Deutsch", fr: "Fran\u00e7ais", es: "Espa\u00f1ol", ar: "\u0627\u0644\u0639\u0631\u0628\u064a\u0629" };

function AnalyticsPanel({ analytics, lang }) {
  const txt = {
    nl: { title: "A/B Taal Analytics", subtitle: "Bezoekersgedrag per taal en detectiemethode", total_pv: "Totaal pageviews", total_cv: "Totaal conversies", language: "Taal", pageviews: "Pageviews", auto: "Auto-detect", manual: "Handmatig", registrations: "Registraties", intakes: "Aanvragen", payments: "Betalingen", revenue: "Omzet", reg_rate: "Registratie %", intake_rate: "Aanvraag %", pay_rate: "Betaling %", no_data: "Nog geen analytics data. Bezoeken worden automatisch bijgehouden.", tip: "Tip: Talen met hogere conversieratio's zijn effectiever. Overweeg om de standaardtaal te wijzigen naar de best presterende." },
    en: { title: "A/B Language Analytics", subtitle: "Visitor behavior by language and detection method", total_pv: "Total pageviews", total_cv: "Total conversions", language: "Language", pageviews: "Pageviews", auto: "Auto-detect", manual: "Manual", registrations: "Registrations", intakes: "Submissions", payments: "Payments", revenue: "Revenue", reg_rate: "Registration %", intake_rate: "Submission %", pay_rate: "Payment %", no_data: "No analytics data yet. Visits are tracked automatically.", tip: "Tip: Languages with higher conversion rates are more effective. Consider changing the default language to the best performing one." },
  };
  const t = txt[lang] || txt[Object.keys(txt).find(k => k !== "nl")] || txt.en;

  if (!analytics || !analytics.by_language || Object.keys(analytics.by_language).length === 0) {
    return (
      <div className="bg-white border border-border p-12 text-center" data-testid="analytics-empty">
        <Globe size={32} className="text-muted-foreground mx-auto mb-3" />
        <p className="text-muted-foreground text-sm">{t.no_data}</p>
      </div>
    );
  }

  const byLang = analytics.by_language;
  const sortedLangs = Object.keys(byLang).sort((a, b) => byLang[b].pageviews - byLang[a].pageviews);

  // Find best performing language
  const bestLang = sortedLangs.reduce((best, l) => {
    const rate = byLang[l].registration_rate || 0;
    return rate > (byLang[best]?.registration_rate || 0) ? l : best;
  }, sortedLangs[0]);

  return (
    <div className="space-y-6" data-testid="analytics-panel">
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white border border-border p-5" data-testid="analytics-total-pv">
          <Globe size={18} className="text-[#0A3FA3] mb-2" />
          <div className="text-2xl font-bold">{analytics.total_pageviews || 0}</div>
          <div className="text-xs text-muted-foreground">{t.total_pv}</div>
        </div>
        <div className="bg-white border border-border p-5" data-testid="analytics-total-cv">
          <TrendingUp size={18} className="text-[#FF3B30] mb-2" />
          <div className="text-2xl font-bold">{analytics.total_conversions || 0}</div>
          <div className="text-xs text-muted-foreground">{t.total_cv}</div>
        </div>
        <div className="bg-white border border-border p-5">
          <div className="text-xs text-muted-foreground mb-1">{lang === "nl" ? "Talen actief" : "Languages active"}</div>
          <div className="text-2xl font-bold">{sortedLangs.length}</div>
        </div>
        <div className="bg-white border border-border p-5">
          <div className="text-xs text-muted-foreground mb-1">{lang === "nl" ? "Beste taal" : "Best language"}</div>
          <div className="text-2xl font-bold text-[#0A3FA3]">{LANG_FLAGS[bestLang] || bestLang}</div>
          <div className="text-xs text-muted-foreground">{LANG_FULL[bestLang] || bestLang}</div>
        </div>
      </div>

      {/* Conversion table */}
      <div className="bg-white border border-border">
        <div className="p-4 border-b border-border bg-[#F9FAFB]">
          <h3 className="text-sm font-bold">{t.title}</h3>
          <p className="text-xs text-muted-foreground">{t.subtitle}</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="analytics-table">
            <thead>
              <tr className="border-b border-border bg-[#F9FAFB]">
                <th className="text-left p-3 text-xs font-bold uppercase tracking-wider">{t.language}</th>
                <th className="text-right p-3 text-xs font-bold uppercase tracking-wider">{t.pageviews}</th>
                <th className="text-right p-3 text-xs font-bold uppercase tracking-wider">{t.auto}</th>
                <th className="text-right p-3 text-xs font-bold uppercase tracking-wider">{t.manual}</th>
                <th className="text-right p-3 text-xs font-bold uppercase tracking-wider">{t.registrations}</th>
                <th className="text-right p-3 text-xs font-bold uppercase tracking-wider">{t.intakes}</th>
                <th className="text-right p-3 text-xs font-bold uppercase tracking-wider">{t.payments}</th>
                <th className="text-right p-3 text-xs font-bold uppercase tracking-wider">{t.revenue}</th>
                <th className="text-right p-3 text-xs font-bold uppercase tracking-wider">{t.reg_rate}</th>
              </tr>
            </thead>
            <tbody>
              {sortedLangs.map((l) => {
                const d = byLang[l];
                const isBest = l === bestLang && d.registration_rate > 0;
                return (
                  <tr key={l} className={`border-b border-border last:border-0 ${isBest ? "bg-[#0A3FA3]/5" : ""}`} data-testid={`analytics-row-${l}`}>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold bg-[#F9FAFB] border border-border px-1.5 py-0.5 rounded-sm">{LANG_FLAGS[l] || l}</span>
                        <span className="text-sm">{LANG_FULL[l] || l}</span>
                        {isBest && <span className="text-[9px] font-bold bg-[#0A3FA3] text-white px-1.5 py-0.5">TOP</span>}
                      </div>
                    </td>
                    <td className="p-3 text-right font-medium">{d.pageviews}</td>
                    <td className="p-3 text-right text-muted-foreground">{d.auto_detected}</td>
                    <td className="p-3 text-right text-muted-foreground">{d.manual}</td>
                    <td className="p-3 text-right">{d.registrations}</td>
                    <td className="p-3 text-right">{d.intakes}</td>
                    <td className="p-3 text-right">{d.payments}</td>
                    <td className="p-3 text-right font-medium">&euro;{(d.revenue || 0).toLocaleString()}</td>
                    <td className="p-3 text-right">
                      <span className={`text-xs font-bold px-1.5 py-0.5 ${d.registration_rate > 5 ? "bg-green-50 text-green-700" : d.registration_rate > 0 ? "bg-yellow-50 text-yellow-700" : "bg-gray-50 text-gray-500"}`}>
                        {d.registration_rate}%
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Tip */}
      <div className="bg-[#0A3FA3]/5 border border-[#0A3FA3]/20 p-4" data-testid="analytics-tip">
        <p className="text-xs text-[#0A3FA3]">{t.tip}</p>
      </div>
    </div>
  );
}
